import { MotionReveal } from "../components/MotionReveal";

const genAiCapabilities = [
  {
    title: "Log Analysis",
    desc: "NLP parsing, semantic clustering, and auto-summaries.",
    tag: "AI",
  },
  {
    title: "AI-Powered RCA",
    desc: "Automated analysis, knowledge graphs, and pattern matching.",
    tag: "RCA",
  },
  {
    title: "Natural Language Ops",
    desc: "ChatOps, plain-English queries, and conversational debug.",
    tag: "NL",
  },
  {
    title: "Runbook Generation",
    desc: "Auto-generate remediation steps and code snippets from incidents.",
    tag: "AUTO",
  },
  {
    title: "Incident Summarization",
    desc: "Executive summaries and timelines for faster resolution.",
    tag: "SUM",
  },
  {
    title: "Predictive Insights",
    desc: "Trend narratives, capacity planning advice, and risk forecasting.",
    tag: "ML",
  },
  {
    title: "Knowledge RAG",
    desc: "Search across runbooks, tickets, and tribal knowledge.",
    tag: "RAG",
  },
];

const llmOptions = ["GPT", "Claude", "Llama", "Mistral", "Private LLM", "On-prem deployment"];

const takeaways = [
  {
    n: "1",
    title: "Unified telemetry foundation",
    desc: "Full-stack observability collecting metrics, logs, traces, events, and network signals.",
    accent: "var(--accent-primary)",
  },
  {
    n: "2",
    title: "AI/ML correlation and causality",
    desc: "Anomaly detection, event correlation, and root cause analysis across topology and time.",
    accent: "var(--accent-tertiary)",
  },
  {
    n: "3",
    title: "Automation at machine speed",
    desc: "Automated remediation and orchestration for faster resolution and reduced human toil.",
    accent: "var(--accent-warning)",
  },
];

const valueMetrics = [
  {
    value: "90%",
    title: "Noise Reduction",
    desc: "Alert correlation reduces alert fatigue.",
    accent: "var(--accent-primary)",
  },
  {
    value: "70%",
    title: "Faster MTTR",
    desc: "Resolution time reduced with AI-driven RCA.",
    accent: "var(--accent-warning)",
  },
  {
    value: "50%",
    title: "Cost Reduction",
    desc: "Lower operational costs through automation.",
    accent: "var(--accent-secondary)",
  },
  {
    value: "99.9",
    title: "Improved Uptime",
    desc: "Predictive analytics prevent outages.",
    accent: "var(--accent-tertiary)",
  },
];

const roadmap = [
  {
    step: "1",
    title: "Foundation",
    months: "Months 1–3",
    items: ["Data collection setup", "Tool integration", "Baseline metrics", "Team training"],
    accent: "var(--accent-primary)",
  },
  {
    step: "2",
    title: "Analytics",
    months: "Months 4–6",
    items: ["ML model deployment", "Anomaly detection", "Event correlation", "Alert tuning"],
    accent: "var(--accent-tertiary)",
  },
  {
    step: "3",
    title: "Automation",
    months: "Months 7–9",
    items: ["Root cause analysis", "Runbook automation", "Self-healing flows", "ITSM integration"],
    accent: "var(--accent-secondary)",
  },
  {
    step: "4",
    title: "Optimization",
    months: "Months 10–12",
    items: ["Predictive analytics", "LLM integration", "Continuous learning", "Full autonomy"],
    accent: "var(--accent-warning)",
  },
];

const layers = [
  {
    title: "Data Sources",
    items: [
      "Applications (APM, logs, traces)",
      "Infrastructure (servers, VMs, containers)",
      "Network (flows, packets, SNMP)",
      "Cloud (AWS, Azure, GCP metrics)",
      "Edge (IoT, remote sites)",
      "User Experience (RUM, synthetic)",
    ],
  },
  {
    title: "Telemetry Collection",
    items: ["Agents & collectors", "OpenTelemetry", "Packet brokers & TAPs", "API integrations"],
  },
  {
    title: "Data Platforms",
    items: ["Dynatrace", "Splunk", "Datadog", "Elastic", "NETSCOUT", "Keysight", "Zabbix"],
  },
  {
    title: "Correlation & Analytics",
    items: ["Topology correlation", "Time-series analysis", "Log analytics", "Metric aggregation"],
  },
  {
    title: "AI/ML & AIOps",
    items: ["Anomaly detection", "Root cause analysis", "Predictive analytics", "Noise reduction"],
  },
  {
    title: "Automation & Remediation",
    items: ["Runbook automation", "ITSM integration", "Self-healing actions", "Orchestration workflows"],
  },
  {
    title: "Visualization & Dashboards",
    items: ["Executive dashboards", "SRE consoles", "Compliance reports", "Capacity views"],
  },
];

export default function ArchitecturePage() {
  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="pt-6 sm:pt-10">
        <MotionReveal>
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.18em] text-[var(--text-secondary)]">
              Reference architecture
            </div>
            <h1 className="mt-6 font-[var(--font-display)] text-4xl sm:text-5xl tracking-tight text-white">
              Reference Architecture
            </h1>
            <p className="mt-4 max-w-3xl text-sm sm:text-base text-[var(--text-secondary)]">
              From edge to executive dashboard — a blueprint that turns telemetry into correlated decisions and automated action.
            </p>
          </div>
        </MotionReveal>
      </div>

      <section className="mt-10 sm:mt-14">
        <MotionReveal>
          <div className="glass-panel rounded-3xl p-6 sm:p-8">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs uppercase tracking-[0.18em] text-[var(--text-secondary)]">
                  New
                </div>
                <div className="mt-3 font-[var(--font-display)] text-2xl sm:text-3xl tracking-tight text-white">
                  Stream Enrichment with AI
                  <span className="block text-[var(--accent-primary)]">GenAI and LLM Integration</span>
                </div>
                <div className="mt-3 max-w-3xl text-sm text-[var(--text-secondary)]">
                  Enrich telemetry streams with language understanding to accelerate RCA, automate runbooks, and convert noisy
                  signals into clear decisions.
                </div>
              </div>
              <div className="hidden sm:block h-14 w-14 rounded-3xl border border-white/10 bg-white/5" style={{ boxShadow: "0 0 28px rgba(0,212,255,0.25)" }} />
            </div>

            <div className="mt-8 grid gap-3 md:grid-cols-3">
              {genAiCapabilities.slice(0, 3).map((c) => (
                <GenAiCard key={c.title} title={c.title} desc={c.desc} tag={c.tag} />
              ))}
            </div>

            <div className="mt-3 grid gap-3 md:grid-cols-2 lg:grid-cols-4">
              {genAiCapabilities.slice(3).map((c) => (
                <GenAiCard key={c.title} title={c.title} desc={c.desc} tag={c.tag} compact />
              ))}
            </div>

            <div className="mt-8 rounded-3xl border border-white/10 bg-[#0b1220]/60 p-5">
              <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">LLM options</div>
              <div className="mt-4 flex flex-wrap gap-2">
                {llmOptions.map((o) => (
                  <div
                    key={o}
                    className="rounded-full border border-white/10 bg-white/3 px-3 py-1 text-xs font-[var(--font-mono)] text-[var(--text-secondary)]"
                  >
                    {o}
                  </div>
                ))}
              </div>
              <div className="mt-3 text-xs text-[var(--text-secondary)]">
                Designed for enterprise controls: data privacy, tenant isolation, auditability, and SOC-aligned governance.
              </div>
            </div>
          </div>
        </MotionReveal>
      </section>

      <section className="mt-10 sm:mt-14">
        <MotionReveal>
          <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/3 p-8 sm:p-10">
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(900px 360px at 20% 20%, rgba(0,212,255,0.18), transparent 60%), radial-gradient(760px 320px at 85% 55%, rgba(99,102,241,0.14), transparent 55%)",
              }}
            />
            <div className="relative grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
              <div>
                <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">Key takeaways</div>
                <div className="mt-3 font-[var(--font-display)] text-3xl sm:text-4xl tracking-tight text-white">
                  Transform IT Operations with Intelligent Automation
                </div>
                <div className="mt-8 grid gap-4">
                  {takeaways.map((t) => (
                    <div key={t.n} className="flex items-start gap-4 rounded-3xl border border-white/10 bg-white/3 p-5">
                      <div
                        className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl border border-white/10 bg-white/5 text-sm font-semibold text-white"
                        style={{ boxShadow: `0 0 24px ${t.accent}33` }}
                      >
                        {t.n}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-white">{t.title}</div>
                        <div className="mt-1 text-sm text-[var(--text-secondary)]">{t.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <a
                    href="/contact"
                    className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-medium text-[#061019] bg-[var(--accent-primary)] hover:brightness-110"
                  >
                    Start Your AIOps Journey Today
                  </a>
                  <a
                    href="/contact"
                    className="inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-medium text-white border border-white/15 bg-white/5 hover:bg-white/8"
                  >
                    Discuss Implementation Strategy
                  </a>
                </div>
              </div>

              <div className="glass-panel rounded-3xl p-6 sm:p-8">
                <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">Complete AIOps reference architecture</div>
                <div className="mt-5 grid gap-3">
                  <ArchBand
                    title="Signal sources"
                    items={["Infrastructure", "Applications", "Cloud", "Security"]}
                    tone="primary"
                  />
                  <ArchBand
                    title="Data pipeline"
                    items={["Collection (OTel, Fluentd)", "Streaming (Kafka)", "Normalization", "Enrichment"]}
                    tone="neutral"
                  />
                  <div className="grid gap-3 sm:grid-cols-2">
                    <MiniTile title="Time-Series DB" subtitle="Metric storage" />
                    <MiniTile title="Log Store" subtitle="Elasticsearch" />
                    <MiniTile title="Graph DB" subtitle="Topology" />
                    <MiniTile title="Data Lake" subtitle="Historical" />
                  </div>
                  <ArchBand
                    title="AI/ML intelligence engine"
                    items={["Anomaly detection", "Event correlation", "Root cause analysis", "Predictive analytics", "LLM/NLP"]}
                    tone="ai"
                  />
                  <ArchBand
                    title="Automation"
                    items={["Alert management", "Auto-remediation", "ITSM integration", "ChatOps"]}
                    tone="action"
                  />
                  <ArchBand
                    title="Visualization"
                    items={["Dashboards", "Reports", "ServiceNow", "Slack/Teams", "PagerDuty"]}
                    tone="neutral"
                  />
                </div>
              </div>
            </div>
          </div>
        </MotionReveal>
      </section>

      <section className="mt-12 sm:mt-16">
        <MotionReveal>
          <div className="grid gap-4 lg:grid-cols-[0.45fr_0.55fr]">
            <div className="grid gap-4">
              {valueMetrics.map((m) => (
                <div key={m.title} className="glass-panel hover-lift rounded-3xl p-6">
                  <div className="flex items-center justify-between gap-4">
                    <div className="text-3xl font-[var(--font-mono)] text-white">{m.value}</div>
                    <span className="h-2 w-20 rounded-full" style={{ background: m.accent, opacity: 0.35 }} />
                  </div>
                  <div className="mt-3 font-[var(--font-display)] text-lg tracking-tight text-white">{m.title}</div>
                  <div className="mt-1 text-sm text-[var(--text-secondary)]">{m.desc}</div>
                </div>
              ))}
            </div>

            <div className="glass-panel rounded-3xl p-6 sm:p-8">
              <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">Business value and key benefits</div>
              <div className="mt-3 font-[var(--font-display)] text-2xl tracking-tight text-white">
                Measurable ROI for leaders
              </div>
              <div className="mt-6 grid gap-4">
                <BenefitGroup
                  title="Operational Excellence"
                  items={["Proactive issue detection", "Automated remediation", "Reduced manual toil", "Faster incident response"]}
                  accent="var(--accent-primary)"
                />
                <BenefitGroup
                  title="Business Impact"
                  items={["Enhanced Customer experience", "Revenue protection", "Competitive advantage", "SLA/SLO protection"]}
                  accent="var(--accent-warning)"
                />
                <BenefitGroup
                  title="Team Productivity"
                  items={["Reduced alert fatigue", "Better work-life balance", "Focus on innovation", "Skill development"]}
                  accent="var(--accent-secondary)"
                />
              </div>
              <div className="mt-8 rounded-3xl border border-white/10 bg-white/3 p-6 text-sm text-[var(--text-secondary)]">
                “Organizations using AIOps report faster problem identification and improved service availability.”
              </div>
            </div>
          </div>
        </MotionReveal>
      </section>

      <section className="mt-12 sm:mt-16">
        <MotionReveal>
          <div className="glass-panel rounded-3xl p-6 sm:p-8">
            <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">Implementation roadmap</div>
            <div className="mt-3 font-[var(--font-display)] text-2xl tracking-tight text-white">Phased approach to AIOps maturity</div>

            <div className="mt-8 grid gap-4 lg:grid-cols-4">
              {roadmap.map((r) => (
                <div key={r.step} className="rounded-3xl border border-white/10 bg-white/3 p-6">
                  <div className="flex items-center justify-between gap-4">
                    <div
                      className="grid h-11 w-11 place-items-center rounded-full border border-white/10 bg-white/5 text-sm font-semibold text-white"
                      style={{ boxShadow: `0 0 28px ${r.accent}55` }}
                    >
                      {r.step}
                    </div>
                    <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">{r.months}</div>
                  </div>
                  <div className="mt-4 font-[var(--font-display)] text-lg tracking-tight text-white">{r.title}</div>
                  <ul className="mt-4 grid gap-2 text-sm text-[var(--text-secondary)]">
                    {r.items.map((i) => (
                      <li key={i} className="flex gap-3">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full" style={{ background: r.accent, opacity: 0.75 }} />
                        <span>{i}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="mt-8 grid gap-3 rounded-3xl border border-white/10 bg-white/3 p-6 sm:grid-cols-2 lg:grid-cols-4">
              <Factor label="Executive sponsorship" />
              <Factor label="Cross-functional teams" />
              <Factor label="Data quality focus" />
              <Factor label="Iterative approach" />
            </div>
          </div>
        </MotionReveal>
      </section>

      <section className="mt-12 sm:mt-16">
        <MotionReveal>
          <div className="glass-panel rounded-3xl p-6 sm:p-8">
            <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">Detailed enterprise AIOps architecture</div>
            <div className="mt-3 font-[var(--font-display)] text-2xl tracking-tight text-white">
              Reference blueprint (enterprise-ready)
            </div>
            <div className="mt-6 grid gap-4 lg:grid-cols-[0.33fr_0.33fr_0.34fr]">
              <StackColumn
                title="Data Sources"
                tone="primary"
                sections={[
                  { title: "Infrastructure", items: ["Servers", "Network devices", "Storage"] },
                  { title: "Applications", items: ["APM", "Logs", "Traces"] },
                  { title: "Cloud / K8s", items: ["AWS/Azure/GCP", "Kubernetes", "Service mesh"] },
                  { title: "Security", items: ["SIEM", "EDR", "IAM"] },
                ]}
              />
              <StackColumn
                title="Data Platform"
                tone="neutral"
                sections={[
                  { title: "Collection", items: ["OpenTelemetry", "Agents", "TAP/Packet brokers", "APIs"] },
                  { title: "Streaming", items: ["Kafka", "Pulsar"] },
                  { title: "Storage", items: ["Time-series", "Logs", "Traces", "Topology"] },
                  { title: "Data Lake", items: ["S3/HDFS", "Governance", "Lineage"] },
                ]}
              />
              <StackColumn
                title="AI/ML Engine + Action Layer"
                tone="ai"
                sections={[
                  {
                    title: "Core ML capabilities",
                    items: [
                      "Anomaly detection",
                      "Event correlation",
                      "Root cause analysis",
                      "Predictive analytics",
                      "LLM layer",
                    ],
                  },
                  { title: "Automation", items: ["Auto-remediation", "Runbooks", "Scale", "Reroute"] },
                  { title: "Integrations", items: ["ServiceNow", "Jira", "Slack/Teams", "PagerDuty"] },
                  { title: "Visualization", items: ["Executive dashboards", "SRE console", "Compliance reporting"] },
                ]}
              />
            </div>
          </div>
        </MotionReveal>
      </section>

      <section className="mt-10 sm:mt-14">
        <MotionReveal>
          <div className="glass-panel rounded-3xl p-6 sm:p-8">
            <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">Interactive diagram (preview)</div>
            <div className="mt-4 grid gap-4">
              {layers.map((l) => (
                <details key={l.title} className="rounded-2xl border border-white/10 bg-white/3 p-5">
                  <summary className="cursor-pointer list-none">
                    <div className="font-[var(--font-display)] text-lg tracking-tight text-white">{l.title}</div>
                    <div className="mt-1 text-sm text-[var(--text-secondary)]">Click to expand</div>
                  </summary>
                  <ul className="mt-4 grid gap-2 text-sm text-[var(--text-secondary)]">
                    {l.items.map((i) => (
                      <li key={i} className="flex gap-3">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[var(--accent-primary)] opacity-70" />
                        <span>{i}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              ))}
            </div>
          </div>
        </MotionReveal>
      </section>

      <div className="h-12" />
    </div>
  );
}

function GenAiCard({ title, desc, tag, compact }) {
  return (
    <div className={`rounded-3xl border border-white/10 bg-white/3 ${compact ? "p-5" : "p-6"}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-[var(--font-display)] text-lg tracking-tight text-white">{title}</div>
          <div className={`mt-2 text-sm text-[var(--text-secondary)] ${compact ? "max-w-none" : "max-w-sm"}`}>{desc}</div>
        </div>
        <div className="grid h-9 w-9 shrink-0 place-items-center rounded-2xl border border-white/10 bg-white/5 text-[10px] font-semibold text-[var(--accent-primary)]">
          {tag}
        </div>
      </div>
    </div>
  );
}

function MiniTile({ title, subtitle }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/3 px-4 py-3">
      <div className="text-sm text-white">{title}</div>
      <div className="mt-1 text-xs text-[var(--text-secondary)]">{subtitle}</div>
    </div>
  );
}

function ArchBand({ title, items, tone }) {
  const border =
    tone === "primary"
      ? "border-[rgba(0,212,255,0.28)]"
      : tone === "ai"
        ? "border-[rgba(16,185,129,0.28)]"
        : tone === "action"
          ? "border-[rgba(245,158,11,0.28)]"
          : "border-white/10";

  return (
    <div className={`rounded-3xl border ${border} bg-white/3 p-4`}>
      <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">{title}</div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {items.map((i) => (
          <div key={i} className="rounded-2xl border border-white/10 bg-white/3 px-3 py-2 text-sm text-white">
            {i}
          </div>
        ))}
      </div>
    </div>
  );
}

function BenefitGroup({ title, items, accent }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-white/3 p-6">
      <div className="flex items-center justify-between gap-4">
        <div className="text-sm font-medium text-white">{title}</div>
        <span className="h-2 w-16 rounded-full" style={{ background: accent, opacity: 0.35 }} />
      </div>
      <div className="mt-4 grid gap-2 text-sm text-[var(--text-secondary)]">
        {items.map((i) => (
          <div key={i} className="flex gap-3">
            <span className="mt-2 h-1.5 w-1.5 rounded-full" style={{ background: accent, opacity: 0.7 }} />
            <span>{i}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Factor({ label }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/3 px-4 py-3">
      <span className="h-2 w-2 rounded-full bg-[var(--accent-primary)] opacity-70" />
      <span className="text-sm text-[var(--text-secondary)]">{label}</span>
    </div>
  );
}

function StackColumn({ title, tone, sections }) {
  const border =
    tone === "primary"
      ? "border-[rgba(0,212,255,0.28)]"
      : tone === "ai"
        ? "border-[rgba(16,185,129,0.28)]"
        : "border-white/10";

  return (
    <div className={`rounded-3xl border ${border} bg-white/3 p-5`}>
      <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">{title}</div>
      <div className="mt-4 grid gap-3">
        {sections.map((s) => (
          <div key={s.title} className="rounded-2xl border border-white/10 bg-white/3 p-4">
            <div className="text-sm font-medium text-white">{s.title}</div>
            <div className="mt-2 grid gap-2 text-sm text-[var(--text-secondary)]">
              {s.items.map((i) => (
                <div key={i} className="flex gap-3">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[var(--accent-primary)] opacity-60" />
                  <span>{i}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
